import React, { useState, useEffect } from 'react';
import { leaveAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { CalendarDays, Plus, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context';

const LEAVE_TYPES = ['annual', 'sick', 'maternity', 'paternity', 'compassionate', 'unpaid', 'toil', 'sabbatical', 'jury_service'];
const STATUS_COLORS = { pending: '#9A6B1A', approved: '#1F7A5A', rejected: '#B3261E' };

export default function AdminLeave() {
  const { user, canManageUsers } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState({ leave_type: 'annual', start_date: '', end_date: '', reason: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [statusFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await leaveAPI.list({ status: statusFilter !== 'all' ? statusFilter : undefined, limit: 50 });
      setRequests(res.data.requests || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const createRequest = async () => {
    if (!form.start_date || !form.end_date) { toast.error('Dates required'); return; }
    try {
      await leaveAPI.create(form);
      toast.success('Leave request submitted');
      setShowCreate(false);
      setForm({ leave_type: 'annual', start_date: '', end_date: '', reason: '' });
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAction = async (id, action) => {
    try {
      await leaveAPI.update(id, { action });
      toast.success(`Request ${action}d`);
      loadData();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-leave-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><CalendarDays className="w-6 h-6 text-[#B89B5E]" /> Leave Management</h1>
          <p className="text-sm text-[#5B616E] mt-1">Request and manage staff absences</p>
        </div>
        <Button onClick={() => setShowCreate(true)} data-testid="create-leave-btn" className="bg-[#121316] text-white hover:bg-[#1B1D22]"><Plus className="w-4 h-4 mr-2" /> Request Leave</Button>
      </div>

      <div className="flex gap-2">
        {['all', 'pending', 'approved', 'rejected'].map(s => (
          <Button key={s} variant={statusFilter === s ? 'default' : 'outline'} size="sm" onClick={() => setStatusFilter(s)} className={statusFilter === s ? 'bg-[#121316] text-white' : ''}>{s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}</Button>
        ))}
      </div>

      <Card className="bg-white border-[#E7E0D6]">
        <CardContent className="pt-6">
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>
          ) : requests.length > 0 ? (
            <div className="space-y-3">
              {requests.map(r => (
                <div key={r.id} className="flex items-center gap-4 p-4 rounded-lg border border-[#E7E0D6] hover:bg-[#FBF7EF] transition-colors" data-testid="leave-request-item">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{r.user_name || 'Staff'}</span>
                      <Badge variant="outline" className="text-[10px] capitalize">{r.leave_type?.replace('_', ' ')}</Badge>
                      <Badge className="text-[10px]" style={{ background: `${STATUS_COLORS[r.status]}15`, color: STATUS_COLORS[r.status] }}>{r.status}</Badge>
                    </div>
                    <p className="text-xs text-[#5B616E] mt-1">{r.start_date} to {r.end_date}</p>
                    {r.reason && <p className="text-xs text-[#5B616E] mt-0.5 italic">{r.reason}</p>}
                  </div>
                  {canManageUsers && r.status === 'pending' && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="text-[#1F7A5A]" onClick={() => handleAction(r.id, 'approve')}><Check className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-[#B3261E]" onClick={() => handleAction(r.id, 'reject')}><X className="w-4 h-4" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-[#5B616E]"><p>No leave requests</p></div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Request Leave</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><label className="text-sm font-medium mb-1 block">Type</label>
              <Select value={form.leave_type} onValueChange={v => setForm({ ...form, leave_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{LEAVE_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-sm font-medium mb-1 block">Start Date</label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><label className="text-sm font-medium mb-1 block">End Date</label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
            </div>
            <div><label className="text-sm font-medium mb-1 block">Reason</label><Textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} placeholder="Optional" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={createRequest} className="bg-[#121316] text-white hover:bg-[#1B1D22]">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
